//@ts-nocheck
import React, {useEffect, useLayoutEffect, useState, useCallback, useRef} from 'react'
import {Outlet, useLocation} from 'react-router-dom'
import {ErrorBoundary, type FallbackProps} from 'react-error-boundary'
import {MenuComponent} from '../assets/ts/components'

import {Content} from './components/Content'
import {Footer} from './components/Footer'
import {HorizontalMenuBar} from './components/HorizontalMenuBar'
import {ScrollTop} from './components/ScrollTop'
import {PageDataProvider} from './core'
import {SocketProvider} from '@/app/modules/widgets/components/SocketProvider'
import {FilePickerHost} from '@/app/modules/widgets/components/file-picker/FilePickerHost'
import {ViewerNotificationHost} from '@/app/modules/widgets/components/external-viewer/ViewerNotificationHost'
import {JobLaunchHost} from '@/app/modules/widgets/components/external-viewer/JobLaunchHost'
import {AIChatPaywall} from '@/app/modules/widgets/components/ai-chat'
import {isAssistantUiIndexError, shouldAutoResetAssistantUiIndexError} from '@/app/modules/widgets/components/ai-chat/assistantUiIndexSafety'
import {AIChatV2Wrapper} from '@/app/modules/widgets/components/ai-chat-v2'
import {ChatProvider} from '@/shared/context/ChatContext'
import {FileImportActionsProvider} from '@/app/modules/widgets/components/FileImportActionsContext'
import {SubscriptionProvider, useSubscription} from '@/shared/utils'
import {useFilesPanelAccess} from '@/app/modules/sharing'
import ResultsPage from '@/app/pages/ResultsPage'
import {FilesPanel, type FilesPanelProps} from './panels/FilesPanel'
import {reconcileWidths, splitFullWidth, PANEL_MIN_WIDTH, COLLAPSE_CENTER_BELOW, RESTORE_CENTER_WIDTH} from './panels/panelWidth'
import {useSharedPanelSplit} from './panels/useSidePanelResize'

const ChatPanel: React.FC<{
  width: number
  onWidthChange: (w: number) => void
  onClose: () => void
  otherPanelWidth: number
  centerCollapsed?: boolean
  onCollapseCenter?: (split: {filesWidth: number; chatWidth: number}) => void
  onPairResize?: (pair: {filesWidth: number; chatWidth: number}) => void
}> = ({
  width,
  onWidthChange,
  onClose,
  otherPanelWidth,
  centerCollapsed,
  onCollapseCenter,
  onPairResize,
}) => {
  const {canViewAiChat, chatAccessDeniedReason, isLoading, isLoadingDetails} = useSubscription()

  if (isLoading || isLoadingDetails) {
    return null
  }

  // An expired ENGINE subscription still opens the chat read-only (history, no new turns).
  if (!canViewAiChat && chatAccessDeniedReason) {
    return (
      <AIChatPaywall
        width={width}
        reason={chatAccessDeniedReason}
        onClose={onClose}
      />
    )
  }

  return (
    <AIChatV2Wrapper
      width={width}
      onWidthChange={onWidthChange}
      onClose={onClose}
      otherPanelWidth={otherPanelWidth}
      centerCollapsed={centerCollapsed}
      onCollapseCenter={onCollapseCenter}
      onPairResize={onPairResize}
    />
  )
}

/**
 * Gate for the Files panel: shown with a subscription (My Files), or when folders are shared with
 * the user (the Shared root). MasterLayout itself renders SubscriptionProvider, so it cannot call
 * useSubscription() at its own level — same reason ChatPanel above exists. Returns the panel's own
 * root div, so it is still a real flex child with no extra wrapper.
 */
const FilesPanelSlot: React.FC<FilesPanelProps> = (props) => {
  // A lapsed subscription (expired over 30 days) turns My Files off; folders shared with the user
  // still show under the Shared root.
  const {hasFileAccess, isLoading, isLoadingDetails, subscriptionLifecycle} = useSubscription()
  const {visible, ownFiles} = useFilesPanelAccess(hasFileAccess)
  // Wait while the subscription loads, and while its details load for the first time (lifecycle
  // still unknown), rather than open on My Files and then take it away.
  const detailsPending = isLoadingDetails && subscriptionLifecycle === null
  if (!visible || (!hasFileAccess && isLoading) || (hasFileAccess && detailsPending)) {
    return null
  }
  return <FilesPanel {...props} ownFiles={ownFiles} />
}

const ChatPanelErrorFallback: React.FC<
  FallbackProps & {width: number; onClose: () => void}
> = ({error, resetErrorBoundary, width, onClose}) => {
  const [showUi, setShowUi] = useState(!isAssistantUiIndexError(error))
  useLayoutEffect(() => {
    if (!isAssistantUiIndexError(error)) return
    if (shouldAutoResetAssistantUiIndexError(error)) {
      resetErrorBoundary()
      return
    }
    setShowUi(true)
  }, [error, resetErrorBoundary])

  if (!showUi) {
    return (
      <div
        className='ai-chat-v2-sidebar d-flex flex-column border-start bg-body'
        style={{width, minWidth: width, maxWidth: '80vw', height: '100%'}}
      />
    )
  }

  return (
    <div
      className='ai-chat-v2-sidebar d-flex flex-column border-start bg-body'
      style={{width, minWidth: width, maxWidth: '80vw', height: '100%'}}
      role='alert'
    >
      <div className='p-4 flex-grow-1'>
        <h5 className='mb-2'>Chat failed to load</h5>
        <p className='text-muted small mb-3'>
          The assistant panel hit an unexpected error. Please contact support with the error message
          displayed below.
        </p>
        <pre className='small text-danger bg-light p-2 rounded' style={{whiteSpace: 'pre-wrap'}}>
          {error.message}
        </pre>
      </div>
      <div className='p-3 border-top d-flex gap-2'>
        <button type='button' className='btn btn-primary btn-sm' onClick={resetErrorBoundary}>
          Try again
        </button>
        <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
          Close chat
        </button>
      </div>
    </div>
  )
}

const MasterLayout = () => {
  const location = useLocation()

  // Check if we're on the results page
  const isResultsPage = location.pathname === '/results'

  // AI Chat panel width management
  const [chatPanelWidth, setChatPanelWidth] = useState(() => {
    const saved = localStorage.getItem('ai-chat-panel-width')
    const defaultWidth = 600
    const maxWidth = window.innerWidth * 0.8 // Max 80% of screen
    const initialWidth = saved ? parseInt(saved, 10) : defaultWidth
    return Math.min(initialWidth, maxWidth)
  })

  // AI Chat panel visibility management
  const [isChatVisible, setIsChatVisible] = useState(() => {
    const saved = localStorage.getItem('ai-chat-visible')
    return saved === 'true' // PRODUCTION: default to collapsed unless explicitly opened
  })

  // Once opened, the chat stays mounted and is only hidden on close, so live streams and loaded
  // conversations survive a toggle.
  const [hasChatMounted, setHasChatMounted] = useState(isChatVisible)
  useEffect(() => {
    if (isChatVisible) setHasChatMounted(true)
  }, [isChatVisible])

  // Files panel width management (mirrors the chat, docked to the left instead)
  const [filesPanelWidth, setFilesPanelWidth] = useState(() => {
    const saved = localStorage.getItem('files-panel-width')
    return saved ? parseInt(saved, 10) : 420
  })

  const [isFilesPanelOpen, setIsFilesPanelOpen] = useState(() => {
    return localStorage.getItem('files-panel-visible') === 'true'
  })

  // When both side panels fill the row, the centre (#wrapper) is hidden but kept mounted so
  // Results iframes do not reload. Restored by top-nav route changes (or closing a side panel).
  const [isCenterCollapsed, setIsCenterCollapsed] = useState(() => {
    return localStorage.getItem('layout-center-collapsed') === 'true'
  })

  // Folder the browser should jump to when the panel is opened from a deep link (job outputs, the
  // dashboard storage card, a bookmarked /files?path=). The nonce lets the same path be requested
  // twice in a row — FileTable only reacts to a *changed* initialPath.
  const [filesPathRequest, setFilesPathRequest] = useState(null)

  const prevPathnameRef = useRef(location.pathname)

  // The clamp is joint: neither panel may grow past what is left after the other panel and a
  // minimum readable centre (or 0 when the centre is collapsed). Persist whatever reconciliation produces.
  const applyWidths = useCallback(
    (files, chat, centerCollapsed = false, minCenterWidth = COLLAPSE_CENTER_BELOW) => {
      const {filesWidth, chatWidth} = reconcileWidths(
        files,
        chat,
        window.innerWidth,
        centerCollapsed,
        minCenterWidth
      )
      setFilesPanelWidth(filesWidth)
      setChatPanelWidth(chatWidth)
      localStorage.setItem('files-panel-width', filesWidth.toString())
      localStorage.setItem('ai-chat-panel-width', chatWidth.toString())
      return {filesWidth, chatWidth}
    },
    []
  )

  const persistCenterCollapsed = useCallback((collapsed: boolean) => {
    setIsCenterCollapsed(collapsed)
    localStorage.setItem('layout-center-collapsed', collapsed ? 'true' : 'false')
  }, [])

  const handleCollapseCenter = useCallback(
    (split: {filesWidth: number; chatWidth: number}) => {
      persistCenterCollapsed(true)
      setFilesPanelWidth(split.filesWidth)
      setChatPanelWidth(split.chatWidth)
      localStorage.setItem('files-panel-width', split.filesWidth.toString())
      localStorage.setItem('ai-chat-panel-width', split.chatWidth.toString())
    },
    [persistCenterCollapsed]
  )

  const handleExpandCenter = useCallback(() => {
    if (!isCenterCollapsed) {
      return
    }
    persistCenterCollapsed(false)
    applyWidths(
      {width: filesPanelWidth, open: isFilesPanelOpen},
      {width: chatPanelWidth, open: isChatVisible},
      false,
      RESTORE_CENTER_WIDTH
    )
  }, [
    isCenterCollapsed,
    persistCenterCollapsed,
    applyWidths,
    filesPanelWidth,
    isFilesPanelOpen,
    chatPanelWidth,
    isChatVisible,
  ])

  const handleSharedSplitChange = useCallback(
    (split: {filesWidth: number; chatWidth: number}) => {
      setFilesPanelWidth(split.filesWidth)
      setChatPanelWidth(split.chatWidth)
      localStorage.setItem('files-panel-width', split.filesWidth.toString())
      localStorage.setItem('ai-chat-panel-width', split.chatWidth.toString())
    },
    []
  )

  const handleSharedSplitPointerDown = useSharedPanelSplit(handleSharedSplitChange)

  const handleChatWidthChange = useCallback(
    (width: number) => {
      // useSidePanelResize already clamped against the files panel; just persist.
      setChatPanelWidth(width)
      localStorage.setItem('ai-chat-panel-width', width.toString())
    },
    []
  )

  const handleChatClose = useCallback(() => {
    setIsChatVisible(false)
    localStorage.setItem('ai-chat-visible', 'false')
    if (isCenterCollapsed) {
      persistCenterCollapsed(false)
    }
  }, [isCenterCollapsed, persistCenterCollapsed])

  const handleChatOpen = useCallback(() => {
    setIsChatVisible(true)
    localStorage.setItem('ai-chat-visible', 'true')
    applyWidths(
      {width: filesPanelWidth, open: isFilesPanelOpen},
      {width: chatPanelWidth, open: true},
      isCenterCollapsed && isFilesPanelOpen
    )
  }, [
    applyWidths,
    filesPanelWidth,
    isFilesPanelOpen,
    chatPanelWidth,
    isCenterCollapsed,
  ])

  const handleChatToggle = useCallback(() => {
    if (isChatVisible) {
      handleChatClose()
    } else {
      handleChatOpen()
    }
  }, [isChatVisible, handleChatClose, handleChatOpen])

  const handleFilesWidthChange = useCallback((width: number) => {
    setFilesPanelWidth(width)
    localStorage.setItem('files-panel-width', width.toString())
  }, [])

  const handleFilesPanelClose = useCallback(() => {
    setIsFilesPanelOpen(false)
    localStorage.setItem('files-panel-visible', 'false')
    if (isCenterCollapsed) {
      persistCenterCollapsed(false)
    }
  }, [isCenterCollapsed, persistCenterCollapsed])

  const handleFilesPanelOpen = useCallback(
    (relativePath?: string) => {
      setIsFilesPanelOpen(true)
      localStorage.setItem('files-panel-visible', 'true')
      if (relativePath !== undefined) {
        setFilesPathRequest((prev) => ({
          path: relativePath,
          nonce: (prev?.nonce ?? 0) + 1,
        }))
      }
      applyWidths(
        {width: filesPanelWidth, open: true},
        {width: chatPanelWidth, open: isChatVisible},
        isCenterCollapsed && isChatVisible
      )
    },
    [applyWidths, filesPanelWidth, chatPanelWidth, isChatVisible, isCenterCollapsed]
  )

  const handleFilesPanelToggle = useCallback(() => {
    if (isFilesPanelOpen) {
      handleFilesPanelClose()
    } else {
      handleFilesPanelOpen()
    }
  }, [isFilesPanelOpen, handleFilesPanelClose, handleFilesPanelOpen])

  /*
    Ctrl/Cmd+B and Ctrl/Cmd+J toggle the two lateral panels — the shortcuts the IDE-native half of
    our users already has muscle memory for, and the reason the header cluster can afford to be
    icon-only. Keystrokes inside a viewer iframe stay in that iframe's document and never reach
    here, which is correct: the viewer owns its own key handling.
  */
  useEffect(() => {
    const isTypingTarget = (target: EventTarget | null) => {
      const element = target as HTMLElement | null
      if (!element || !element.tagName) {
        return false
      }
      return (
        element.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)
      )
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) {
        return
      }
      if (isTypingTarget(event.target)) {
        return
      }

      const key = event.key.toLowerCase()
      if (key === 'b') {
        event.preventDefault()
        handleFilesPanelToggle()
      } else if (key === 'j') {
        event.preventDefault()
        handleChatToggle()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleFilesPanelToggle, handleChatToggle])

  useEffect(() => {
    setTimeout(() => {
      MenuComponent.reinitialization()
    }, 500)
  }, [])

  useEffect(() => {
    setTimeout(() => {
      MenuComponent.reinitialization()
    }, 500)
  }, [location.key])

  // Top-nav route change restores the centre at ≥ RESTORE_CENTER_WIDTH so Jobs/Results/Dashboard return.
  // Same-route clicks (e.g. Results while already on /results) are handled via expandCenter on the
  // menu item itself — navigate() does not change pathname, so this effect alone is not enough.
  useEffect(() => {
    if (prevPathnameRef.current === location.pathname) {
      return
    }
    prevPathnameRef.current = location.pathname
    handleExpandCenter()
  }, [location.pathname, handleExpandCenter])

  // Collapsed centre only makes sense with both side panels open.
  useEffect(() => {
    if (isCenterCollapsed && (!isFilesPanelOpen || !isChatVisible)) {
      persistCenterCollapsed(false)
      applyWidths(
        {width: filesPanelWidth, open: isFilesPanelOpen},
        {width: chatPanelWidth, open: isChatVisible},
        false
      )
    }
  }, [
    isCenterCollapsed,
    isFilesPanelOpen,
    isChatVisible,
    persistCenterCollapsed,
    applyWidths,
    filesPanelWidth,
    chatPanelWidth,
  ])

  // Re-clamp both panels together on mount and on viewport resize. Mount matters as much as resize:
  // two panels restored from localStorage on a small screen can start out starving the centre.
  // While the centre is collapsed, redistribute Files/Chat across the new width (do not auto-expand).
  useEffect(() => {
    const bothOpen = isFilesPanelOpen && isChatVisible
    const collapsed = isCenterCollapsed && bothOpen

    const handleWindowResize = () => {
      const {filesWidth, chatWidth} = reconcileWidths(
        {width: filesPanelWidth, open: isFilesPanelOpen},
        {width: chatPanelWidth, open: isChatVisible},
        window.innerWidth,
        collapsed
      )
      if (filesWidth !== filesPanelWidth) {
        setFilesPanelWidth(filesWidth)
        localStorage.setItem('files-panel-width', filesWidth.toString())
      }
      if (chatWidth !== chatPanelWidth) {
        setChatPanelWidth(chatWidth)
        localStorage.setItem('ai-chat-panel-width', chatWidth.toString())
      }
    }

    handleWindowResize()
    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [
    chatPanelWidth,
    filesPanelWidth,
    isChatVisible,
    isFilesPanelOpen,
    isCenterCollapsed,
  ])

  const showSharedSplit =
    isCenterCollapsed && isFilesPanelOpen && isChatVisible

  return (
    <PageDataProvider>
      <SubscriptionProvider>
        <ChatProvider
          onOpenChat={handleChatOpen}
          isChatVisible={isChatVisible}
          onToggleChat={handleChatToggle}
          isFilesPanelOpen={isFilesPanelOpen}
          onToggleFilesPanel={handleFilesPanelToggle}
          onOpenFilesPanel={handleFilesPanelOpen}
          onCloseFilesPanel={handleFilesPanelClose}
          onExpandCenter={handleExpandCenter}
        >
          <FileImportActionsProvider>
          <SocketProvider>
          {/* 
            Root page container with fixed viewport height and proper overflow management.
            Uses flexbox column layout to stack header, main content, and footer.
          */}
          <div className='page d-flex flex-column flex-root ob-app-shell'>
          {/* Unified Menu Bar — logo (left), nav (center), profile (right) */}
          <HorizontalMenuBar />

          {/* 
            Main content container with AI chat panel.
            Height is constrained to fit exactly between header and footer,
            preventing any page-level scrolling.
          */}
          <div className='d-flex flex-column flex-lg-row ob-app-panels'>
            {/*
              Files panel — left-hand mirror of the AI chat panel below. Kept mounted regardless of
              open state (it hides itself with display:none) so the browser's selection, scroll and
              clipboard survive a collapse, and so route changes never remount it.
            */}
            <FilesPanelSlot
              isOpen={isFilesPanelOpen}
              width={filesPanelWidth}
              onWidthChange={handleFilesWidthChange}
              onClose={handleFilesPanelClose}
              otherPanelWidth={isChatVisible ? chatPanelWidth : 0}
              initialPath={filesPathRequest?.path}
              pathRequestNonce={filesPathRequest?.nonce}
              centerCollapsed={showSharedSplit}
              onCollapseCenter={handleCollapseCenter}
              onPairResize={handleSharedSplitChange}
            />

            {showSharedSplit && (
              <div
                className='ob-panel-split-handle'
                data-testid='omics-panel-split-handle'
                role='separator'
                aria-orientation='vertical'
                aria-valuemin={PANEL_MIN_WIDTH}
                aria-valuemax={window.innerWidth - PANEL_MIN_WIDTH}
                aria-valuenow={filesPanelWidth}
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
                    return
                  }
                  event.preventDefault()
                  const step = event.shiftKey ? 40 : 10
                  const delta = event.key === 'ArrowLeft' ? -step : step
                  handleSharedSplitChange(
                    splitFullWidth(filesPanelWidth + delta, window.innerWidth)
                  )
                }}
              >
                <div
                  className='ob-panel-split-handle__grip'
                  onMouseDown={handleSharedSplitPointerDown}
                  title='Drag to resize Files and Chat'
                />
              </div>
            )}

            {/* 
              Main Content Area - This wrapper div handles all main content scrolling.
              When content exceeds available height, this div scrolls independently
              while maintaining the fixed header and footer layout.
              When the centre is collapsed (Files + Chat fill the row), keep it mounted
              so Results iframes do not reload — only hide it visually.
            */}
            <div
              className='wrapper d-flex flex-column flex-row-fluid'
              id='wrapper'
              style={
                showSharedSplit
                  ? {
                      flex: '0 0 0',
                      width: 0,
                      minWidth: 0,
                      maxWidth: 0,
                      overflow: 'hidden',
                      visibility: 'hidden',
                      padding: 0,
                      minHeight: 0,
                    }
                  : {
                      flex: 1,
                      overflow: 'auto',
                      minWidth: 0,
                      paddingTop: '10px',
                      paddingBottom: '10px',
                      minHeight: 0,
                    }
              }
            >
              {/* 
                Content container - No scrolling here since wrapper handles it.
                This allows for proper flex layout without scroll conflicts.
              */}
              <div
                id='content'
                className='content d-flex flex-column flex-column-fluid'
                style={{
                  flex: 1,
                  overflow: 'visible', // Content flows naturally within scrolling wrapper
                }}
              >
                <div className='post d-flex flex-column-fluid' id='post'>
                  <Content>
                      <Outlet />
                      {/* 
                      Results Page - Always rendered but visibility controlled.
                      This prevents iframe reloading when switching between routes.

                      Moving the grid manager up here and using portals was not working
                      because the browser has a mechanism to automatically reload iframe
                      content when it moves inside the DOM tree, although there are plans
                      to change this (https://github.com/httptoolkit/react-reverse-portal/issues/14)
                      */}
                      <ResultsPage isVisible={isResultsPage} />
                  </Content>
                </div>
              </div>
            </div>
            {hasChatMounted && (
              <div style={{display: isChatVisible ? 'contents' : 'none'}}>
              <ErrorBoundary
                fallbackRender={({error, resetErrorBoundary}) => (
                  <ChatPanelErrorFallback
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    width={chatPanelWidth}
                    onClose={handleChatClose}
                  />
                )}
              >
                <ChatPanel
                  width={chatPanelWidth}
                  onWidthChange={handleChatWidthChange}
                  onClose={handleChatClose}
                  otherPanelWidth={isFilesPanelOpen ? filesPanelWidth : 0}
                  centerCollapsed={showSharedSplit}
                  onCollapseCenter={handleCollapseCenter}
                  onPairResize={handleSharedSplitChange}
                />
              </ErrorBoundary>
              </div>
            )}
          </div>

          {/* 
            Fixed Footer - Positioned at bottom of viewport.
            Compact design with minimal height and padding.
          */}
          <Footer />
          </div>

          {/* Scroll to top component for main content scrolling */}
          <ScrollTop />

          {/*
            The page's single file-pick dialog. A wizard inside a viewer iframe cannot browse
            storage, so it asks and this answers; mounted here so the dialog is centred on the page
            rather than inside whichever iframe asked, and so there is exactly one of it.
          */}
          <FilePickerHost />

          {/*
            Viewer messages addressed to the user rather than to a tab (an action or report link
            that produced no viewer, but has something to say). One of it, for the same reason as
            above: several viewer managers can be mounted at once.
          */}
          <ViewerNotificationHost />

          {/*
            Jobs a viewer action wizard submitted. Here rather than in a viewer manager because a
            run outlives the viewer that started it — and, like the two above, so there is exactly
            one of it when several managers are mounted.
          */}
          <JobLaunchHost />
        </SocketProvider>
          </FileImportActionsProvider>
      </ChatProvider>
      </SubscriptionProvider>
    </PageDataProvider>
  )
}

export {MasterLayout}
